import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Gift, TrendingUp, Users, Save } from 'lucide-react';

const fmt = (n) => (Number(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AdminCashback() {
    const [settings, setSettings] = useState({ global_max_percent: 15, global_enabled: true });
    const [overview, setOverview] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const [{ data: s }, { data: o }] = await Promise.all([
                api.get('/admin/cashback/settings'),
                api.get('/admin/cashback/overview'),
            ]);
            setSettings(s);
            setOverview(o);
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        setBusy(true);
        try {
            await api.put('/admin/cashback/settings', settings);
            toast.success('Ajustes globales guardados');
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); } finally { setBusy(false); }
    };

    return (
        <div className="space-y-6" data-testid="admin-cashback-page">
            <div className="flex items-center gap-2">
                <Gift className="h-6 w-6 text-[hsl(var(--primary))]" />
                <h1 className="text-2xl font-semibold">Cashback global</h1>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Card><CardContent className="p-4">
                    <div className="text-xs uppercase text-muted-foreground">Vendedores con cashback</div>
                    <div className="text-2xl font-semibold">{overview?.enabled_sellers ?? '—'}</div>
                </CardContent></Card>
                <Card><CardContent className="p-4">
                    <div className="text-xs uppercase text-muted-foreground">Puntos entregados</div>
                    <div className="text-2xl font-semibold">{fmt(overview?.total_cashback_given)}</div>
                </CardContent></Card>
                <Card><CardContent className="p-4">
                    <div className="text-xs uppercase text-muted-foreground">Transacciones</div>
                    <div className="text-2xl font-semibold">{overview?.total_transactions ?? '—'}</div>
                </CardContent></Card>
            </div>

            <Card>
                <CardContent className="p-5 space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-base font-semibold">Habilitar cashback en toda la plataforma</h3>
                            <p className="text-xs text-muted-foreground mt-1">Al desactivar, los vendedores no podrán aplicar cashback aunque tengan su config activa.</p>
                        </div>
                        <Switch checked={settings.global_enabled} onCheckedChange={(v) => setSettings({ ...settings, global_enabled: v })} data-testid="cashback-global-switch" />
                    </div>
                    <div>
                        <Label>Tope máximo por vendedor ({settings.global_max_percent}%)</Label>
                        <Slider value={[settings.global_max_percent]} min={0} max={50} step={0.5} onValueChange={(v) => setSettings({ ...settings, global_max_percent: v[0] })} data-testid="cashback-global-max" />
                        <p className="text-xs text-muted-foreground mt-1">Ningún vendedor podrá configurar un porcentaje superior a este.</p>
                    </div>
                    <Button onClick={save} disabled={busy} data-testid="cashback-global-save">
                        <Save className="h-4 w-4 mr-1" />{busy ? 'Guardando…' : 'Guardar ajustes'}
                    </Button>
                </CardContent>
            </Card>

            <Card>
                <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                        <Users className="h-5 w-5" />
                        <h3 className="text-base font-semibold">Vendedores con cashback activo</h3>
                    </div>
                    {overview?.sellers?.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">Ningún vendedor tiene cashback activo aún.</div>}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {overview?.sellers?.map(s => (
                            <div key={s.id} className="flex items-center justify-between p-3 rounded border">
                                <div className="flex items-center gap-2">
                                    <div className="h-8 w-8 rounded-full bg-[hsl(var(--accent))] flex items-center justify-center overflow-hidden">
                                        {s.avatar_url ? <img src={s.avatar_url} alt="" className="h-full w-full object-cover" /> : <span className="text-xs font-semibold">{s.nickname?.[0]?.toUpperCase()}</span>}
                                    </div>
                                    <div className="text-sm font-medium">@{s.nickname}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Badge className="bg-[hsl(var(--accent))] text-[hsl(var(--primary))]">{s.cashback_config?.percent}%</Badge>
                                </div>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {overview?.top_sellers?.length > 0 && (
                <Card>
                    <CardContent className="p-5">
                        <div className="flex items-center gap-2 mb-3">
                            <TrendingUp className="h-5 w-5" />
                            <h3 className="text-base font-semibold">Top vendedores por cashback entregado</h3>
                        </div>
                        <div className="space-y-1">
                            {overview.top_sellers.map((t, i) => (
                                <div key={t._id || i} className="flex items-center justify-between p-2 rounded hover:bg-[hsl(var(--secondary))]">
                                    <div>#{i + 1} @{t.nickname || t._id?.slice(0, 8)}</div>
                                    <div className="font-semibold">{fmt(t.given)} pts · <span className="text-xs text-muted-foreground">{t.count} órdenes</span></div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
